import React from 'react';
import { Content, Page } from '@backstage/core-components';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  Grid,
  makeStyles,
  Typography,
  Box,
  Card,
  CardActionArea,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DescriptionIcon from '@material-ui/icons/Description';
import GridViewIcon from '@material-ui/icons/Apps';
import RadarIcon from '@material-ui/icons/TrackChanges';
import PackageIcon from '@material-ui/icons/Widgets';
import InsightsIcon from '@material-ui/icons/ShowChart';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { WelcomeBanner } from './WelcomeBanner';
import { StatsOverview } from './StatsOverview';
import { FeaturedTemplates } from './FeaturedTemplates';
import { dark, light } from '../../theme/tokens';

const useStyles = makeStyles(theme => {
  const t = theme.palette.type === 'dark' ? dark : light;
  return {
    searchBarContainer: {
      display: 'flex',
      marginBottom: theme.spacing(3.5),
    },
    searchBar: {
      width: '100%',
      maxWidth: '100%',
      backgroundColor: 'transparent',
    },
    section: {
      marginBottom: theme.spacing(3.5),
    },
    sectionTitle: {
      fontSize: '0.9375rem',
      fontWeight: 700,
      color: t.text,
      marginBottom: theme.spacing(1.75),
    },
    quickCard: {
      height: '100%',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
      '&:hover': {
        borderColor: t.primaryBorder,
        boxShadow: t.cardShadowLg,
        transform: 'translateY(-2px)',
      },
    },
    quickCardArea: {
      position: 'relative',
      padding: theme.spacing(2.25),
      height: '100%',
      display: 'block',
      textAlign: 'left',
    },
    quickIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 40,
      borderRadius: 10,
      background: t.primary50,
      color: t.primary,
      marginBottom: theme.spacing(1.5),
    },
    quickTitle: {
      fontSize: '0.875rem',
      fontWeight: 700,
      color: t.text,
      marginBottom: theme.spacing(0.5),
    },
    quickDesc: {
      fontSize: '0.78rem',
      lineHeight: 1.5,
      color: t.textMuted,
    },
    quickArrow: {
      position: 'absolute',
      top: theme.spacing(2.25),
      right: theme.spacing(2),
      color: t.textSubtle,
      fontSize: 18,
    },
  };
});

interface QuickAction {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

const QuickActionCard = ({ title, description, icon: Icon, onClick }: QuickAction) => {
  const classes = useStyles();
  return (
    <Card className={classes.quickCard}>
      <CardActionArea className={classes.quickCardArea} onClick={onClick}>
        <ArrowForwardIcon className={classes.quickArrow} />
        <Box className={classes.quickIcon}>
          <Icon />
        </Box>
        <Typography className={classes.quickTitle}>{title}</Typography>
        <Typography className={classes.quickDesc}>{description}</Typography>
      </CardActionArea>
    </Card>
  );
};

export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const quickActions: QuickAction[] = [
    {
      title: 'Create service',
      description: 'Scaffold new services and components from vetted templates',
      icon: AddCircleIcon,
      onClick: () => navigate('/create'),
    },
    {
      title: 'Browse catalog',
      description: 'Explore every service, API, and resource in the org',
      icon: GridViewIcon,
      onClick: () => navigate('/catalog'),
    },
    {
      title: 'Documentation',
      description: 'Technical docs, runbooks, and onboarding guides',
      icon: DescriptionIcon,
      onClick: () => navigate('/docs'),
    },
    {
      title: 'Tech radar',
      description: 'Technology adoption signals and recommendations',
      icon: RadarIcon,
      onClick: () => navigate('/tech-radar'),
    },
    {
      title: 'Self service',
      description: 'Request access to Jenkins, AWS, Azure, and databases',
      icon: PackageIcon,
      onClick: () => navigate('/self-service'),
    },
    {
      title: 'Cost insights',
      description: 'Monitor and optimize cloud spend across teams',
      icon: InsightsIcon,
      onClick: () => navigate('/sip'),
    },
  ];

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <WelcomeBanner />

          <Box className={classes.searchBarContainer}>
            <HomePageSearchBar
              classes={{ root: classes.searchBar }}
              placeholder="Search for services, APIs, docs, and more…"
            />
          </Box>

          <Box className={classes.section}>
            <StatsOverview />
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Quick actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map(action => (
                <Grid item xs={12} sm={6} md={4} key={action.title}>
                  <QuickActionCard {...action} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box className={classes.section}>
            <FeaturedTemplates />
          </Box>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
